// Long-form editorial companion to the 12 Observations channels.
// Rendered inside a "Read more" modal on /observations. Each entry
// corresponds to a channel `key` in Observations.jsx.

export const OBSERVATIONS_INTRO = {
  eyebrow: "/ Different ways to observe nature",
  title: "Nature is constantly communicating with us.",
  body: `A flowering tree, fewer butterflies, an unusually early migration, a dry stream, a sudden increase in mosquitoes, warmer ocean water, changing cloud formations, or repeated flooding can each tell part of a much larger story.

You don't need to be a scientist to observe nature. You simply need to look, listen, photograph, record by adding a note, a webpage link to a news or video and compare.

NaturePulse encourages people everywhere to document what they see around them — from their backyard and neighborhood to farms, forests, rivers, mountains and coastlines. A single observation may seem small, but thousands or millions of observations collected over time can reveal important patterns.

When making an observation, consider recording the date, time, location, photographs or video, species if known, weather conditions and anything unusual you notice. Repeat observations from the same location are especially valuable because they help show how nature changes over days, seasons and years.`,
};

export const CHANNEL_ARTICLES = {
  plants: {
    emoji: "🌱",
    title: "Plants & Vegetation",
    subtitle: "Watch the Green World Around You",
    lede: `Plants are among the easiest and most valuable parts of nature to observe. They respond visibly to rainfall, temperature, sunlight, soil conditions, pollution, insects, disease and changing seasons.

Look beyond simply identifying a plant. Observe what the plant is doing. Is it producing new leaves? Flowering? Developing fruit? Losing leaves? Wilting? Recovering after rain? Spreading into a new area? Photographing the same plant periodically can create a remarkable record of its life cycle.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Notice:
• New shoots and leaf growth
• Budding and flowering
• Fruit and seed development
• Leaf color changes
• Seasonal leaf fall
• Wilting or drought stress
• Frost or heat damage
• Plant diseases
• Fungal growth
• Insect damage
• Invasive plants
• Wildflowers appearing or disappearing
• Changes in grasslands and ground cover
• Vegetation returning after fire, flooding or construction

Try identifying the plant by common and scientific name when possible.`,
      },
      {
        heading: "Why It Matters",
        body: `Plants form the foundation of most terrestrial ecosystems. Changes in vegetation can indicate changing rainfall, temperature, soil moisture, pollution and wildlife activity.

Even something as simple as recording when a familiar tree first flowers each year can become useful evidence of changing seasonal patterns.`,
      },
    ],
    outro: "Nature is telling us: watch when plants wake, flower, fruit, struggle and disappear.",
  },
  trees: {
    emoji: "🌳",
    title: "Trees & Forests",
    subtitle: "Observe Nature's Long-Term Record Keepers",
    lede: `Trees may live for decades or centuries, making them extraordinary witnesses to environmental change. Observe individual trees in your yard, street or neighborhood as well as forests, plantations, mangroves and wooded areas.

A healthy tree supports much more than itself. Its branches may provide nesting places, its flowers feed pollinators, its fruit feeds animals and its roots protect soil and retain water.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Record:
• New leaves and shoots
• Flowering periods
• Fruit and seed production
• Leaf fall
• Broken or fallen branches
• Storm damage · Lightning damage
• Drought stress
• Bark damage
• Fungal infections
• Dead or dying trees
• Tree diseases · Invasive pests
• Bird nests and animal cavities
• Forest clearing
• New tree planting
• Wildfire damage
• Natural forest regeneration
• Mangrove loss or expansion

Photograph the same tree from approximately the same position several times each year.`,
      },
      {
        heading: "Forest Health",
        body: `When visiting forests, look at the entire ecosystem. Are there young trees growing beneath older trees? Is the forest floor moist or unusually dry? Are invasive plants taking over? Are streams still flowing? Are birds, insects and mammals abundant?`,
      },
      {
        heading: "Why It Matters",
        body: `Trees influence temperature, rainfall, soil stability, biodiversity and carbon storage. Changes in forests can affect entire communities far beyond the forest itself.`,
      },
    ],
    outro: "Nature is telling us: a healthy forest is not simply a collection of trees — it is a living community.",
  },
  agriculture: {
    emoji: "🌾",
    title: "Agriculture & Edibles",
    subtitle: "Observe the Nature That Feeds Us",
    lede: `Agriculture is one of the clearest places where human activity and natural systems meet. Farmers have always been careful observers of weather, soil, insects, water and seasonal changes. NaturePulse can extend this tradition by helping communities document crops and edible plants around the world.

Observe farms, orchards, vegetable gardens, backyard fruit trees, community gardens and wild edible plants.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Record:
• Planting dates · Germination · Crop growth
• Flowering · Pollination · Fruit development
• Harvest dates · Crop yields
• Drought stress · Excessive rainfall
• Flood damage · Heat damage · Frost damage
• Pest outbreaks · Plant diseases
• Irrigation conditions · Soil erosion
• Changes in planting seasons

Also document edible plants such as: fruits · vegetables · herbs · greens · nuts · berries · seeds · tubers · wild edibles.`,
      },
      {
        heading: "Observe Food Seasons",
        body: `When does the first mango appear? When are berries ripe? Are tomatoes ripening earlier? Is a traditional crop becoming difficult to grow?

These everyday observations can reveal changing agricultural seasons.`,
      },
    ],
    outro: "Nature is telling us: changes in climate eventually appear on our farms — and on our plates.",
  },
  birds: {
    emoji: "🐦",
    title: "Birds & Nesting",
    subtitle: "Listen Before You Look",
    lede: `Birds are among nature's most visible — and audible — indicators of environmental health. You can observe birds almost anywhere: cities, gardens, farms, forests, wetlands, beaches and even from an apartment window.

Begin by listening. Bird calls often reveal species that are hidden in trees or vegetation.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Record:
• Species seen · Number of birds
• Bird calls and songs
• Feeding behavior · Courtship
• Nest building · Eggs · Chicks · Fledglings
• Migration arrivals and departures
• Flocks · Birds using feeders · Birds bathing
• Predators · Injured or dead birds
• Unusual species appearing in an area

Never disturb nests or approach them closely simply to obtain photographs.`,
      },
      {
        heading: "Birds as Environmental Indicators",
        body: `A neighborhood supporting many bird species usually provides some combination of trees, insects, water, nesting areas and food.

A noticeable decline in birds may therefore encourage us to investigate other changes. Have insects declined? Have trees been removed? Has construction increased? Has a wetland disappeared?`,
      },
    ],
    outro: "Nature is telling us: listen to the birds — and notice when familiar voices become quiet.",
  },
  wildlife: {
    emoji: "🦌",
    title: "Animals & Wildlife",
    subtitle: "Discover Who Shares Your Environment",
    lede: `Wild animals often live much closer to us than we realize. Deer, squirrels, rabbits, foxes, coyotes, monkeys, elephants, reptiles and countless other animals leave evidence of their presence even when we don't see them directly.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Look for:
• Animals seen · Tracks and footprints
• Droppings · Burrows and dens
• Feeding marks · Scratches on trees
• Fur or feathers · Migration · Breeding behavior
• Mothers with young · Road crossings
• Wildlife corridors · Animals entering urban areas
• Injured wildlife · Roadkill · Human-wildlife conflicts

Observe from a safe distance and avoid feeding or approaching wild animals.`,
      },
      {
        heading: "Changing Wildlife Behavior",
        body: `Animals sometimes move into new areas because their habitats or food sources have changed.

Repeated observations of animals appearing in towns, farms or roads may indicate habitat loss, drought, wildfire, development or changes in food availability.`,
      },
    ],
    outro: "Nature is telling us: where animals go — and where they stop going — can reveal changes in the landscape.",
  },
  insects: {
    emoji: "🐝",
    title: "Insects & Pollinators",
    subtitle: "Pay Attention to the Smallest Workers",
    lede: `Insects may be small, but their ecological importance is enormous. Bees, butterflies, moths, beetles, flies and other insects pollinate plants, recycle organic material and provide food for birds, fish, reptiles and mammals.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Record:
• Bees visiting flowers · Butterfly species
• Moths · Beetles · Dragonflies · Fireflies
• Ant activity · Caterpillars · Ladybugs
• Mosquito abundance · Insect emergence
• Pollination activity · Swarms
• Pest outbreaks · Invasive insects

Try observing the same flowering plants periodically. How many pollinators visit in five or ten minutes?`,
      },
      {
        heading: "Notice Absence Too",
        body: `One of the most important observations can be something that isn't there anymore. If a garden once attracted dozens of butterflies and now attracts very few, record it.

Changes in pesticide use, vegetation, temperature, rainfall and habitat can influence insect populations.`,
      },
    ],
    outro: "Nature is telling us: sometimes the smallest creatures provide the earliest warning.",
  },
  aquatic: {
    emoji: "🐟",
    title: "Fish & Aquatic Life",
    subtitle: "Look Beneath the Surface",
    lede: `Rivers, lakes, ponds, wetlands, estuaries and oceans contain complex ecosystems that are often difficult to see from land. Fish populations and other aquatic organisms can reveal important information about water quality and ecosystem health.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Document:
• Fish species · Schools of fish · Spawning activity
• Fishing catches · Changes in fish size or abundance
• Dead fish · Frogs and tadpoles · Turtles
• Crabs · Shrimp · Shellfish · Jellyfish
• Aquatic insects · Algae · Aquatic vegetation
• Invasive species`,
      },
      {
        heading: "Watch the Water Too",
        body: `Record unusual water color · odor · foam · algae blooms · oil or chemical films · plastic pollution · sediment · dead aquatic animals.

Mass fish deaths or sudden water discoloration may deserve immediate attention from local environmental authorities.`,
      },
    ],
    outro: "Nature is telling us: the condition of aquatic life often reflects the condition of the water itself.",
  },
  sky: {
    emoji: "🌦️",
    title: "Climate, Weather & Sky",
    subtitle: "Become an Observer of the Atmosphere",
    lede: `The sky provides a constantly changing natural laboratory. Rather than recording only "sunny" or "rainy," observe the details.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Record:
• Temperature · Rain · Snow · Hail
• Thunderstorms · Lightning · Cloudbursts
• Wind · Humidity · Frost · Ice · Fog · Smog
• Heat waves · Cold spells · Drought conditions`,
      },
      {
        heading: "Observe the Sky",
        body: `Photograph cloud types, storm formations, rainbows, sunrise, sunset, moon phases, solar and lunar eclipses, unusual atmospheric phenomena.`,
      },
      {
        heading: "Create Your Own Weather Almanac",
        body: `Repeated observations from the same place can become a personal or community weather almanac. Record the first frost, first snow, hottest periods, heavy rains and unusual weather. Over several years, patterns may begin to emerge.`,
      },
    ],
    outro: "Nature is telling us: weather is today's story; climate is the pattern revealed over time.",
  },
  water: {
    emoji: "💧",
    title: "Water · Lakes, Rivers & Floods",
    subtitle: "Follow the Movement of Water",
    lede: `Water connects landscapes, ecosystems, agriculture and communities. A small stream can reveal rainfall conditions upstream. A shrinking lake can indicate prolonged drought. Muddy water may reveal erosion or construction. Repeated street flooding may show that drainage systems or rainfall patterns are changing.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Record:
• River and stream levels · Lake and pond levels
• Reservoir levels · Springs · Waterfalls
• Wetlands · Dry streambeds
• Water clarity · Sediment · Algae
• Pollution · Floating waste
• Bank erosion · Dam conditions`,
      },
      {
        heading: "Flood Observations",
        body: `Document: location · date and time · approximate water depth · roads affected · buildings affected · agricultural flooding · river overflow · flash flooding · tidal flooding · drainage problems · debris carried by floodwater · how long the water remains.

Never enter moving floodwater to make an observation.`,
      },
    ],
    outro: "Nature is telling us: where water goes — and where it can no longer go — tells us how landscapes are changing.",
  },
  geology: {
    emoji: "🪨",
    title: "Land & Geological Changes",
    subtitle: "Observe the Ground Beneath Us",
    lede: `Land may appear permanent, but it is constantly changing. Water, wind, earthquakes, volcanic activity, erosion and human development continuously reshape the Earth's surface.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Document:
• Soil erosion · Riverbank erosion · Coastal erosion
• Landslides · Mudslides · Rockfalls · Sinkholes
• Ground cracks · Land subsidence · Soil movement
• Changing dunes · Exposed rock formations
• Sediment deposits · Earthquake damage
• Volcanic activity · Ash deposits

Also document human-caused changes such as excavation, mining, quarrying, hill cutting and major land filling.`,
      },
      {
        heading: "Before-and-After Photography",
        body: `Landscape photography becomes especially valuable when repeated from the same position. Today's ordinary photograph may become tomorrow's important historical environmental record.`,
      },
    ],
    outro: "Nature is telling us: the ground beneath our feet is not as permanent as it appears.",
  },
  hazards: {
    emoji: "⚠️",
    title: "Roads & Environmental Hazards",
    subtitle: "Document When Nature and Human Activity Collide",
    lede: `NaturePulse can also help communities document events that affect people, wildlife and ecosystems. Some events occur naturally. Others are caused or intensified by human activity. Often the causes overlap.`,
    sections: [
      {
        heading: "What to Observe",
        body: `Document:
• Wildfires · Forest fires · Floods · Flash floods
• Landslides · Mudslides · Avalanches
• Earthquakes · Volcanic eruptions
• Severe storms · Tornadoes · Hurricanes and cyclones
• Extreme heat · Extreme cold · Drought
• Fallen trees · Road washouts · Coastal flooding
• Pollution incidents · Chemical spills
• Fish kills · Wildlife deaths

Also record environmental impacts from accidents and infrastructure failures where appropriate.`,
      },
      {
        heading: "Safety Comes First",
        body: `Never put yourself in danger to obtain a photograph or report. Observe from a safe location and follow evacuation orders and instructions from emergency authorities.

NaturePulse observations should complement — not replace — official emergency reporting systems.`,
      },
    ],
    outro: "Nature is telling us: extreme events can reveal vulnerabilities that ordinary days keep hidden.",
  },
  seasonal: {
    emoji: "🍂",
    title: "Seasonal & Climate Changes",
    subtitle: "Observe Nature Across Time",
    lede: `Perhaps the most powerful NaturePulse observations are those repeated year after year. Nature operates according to rhythms: flowering, migration, rainfall, nesting, fruiting, leaf fall, freezing and thawing. When these events begin occurring earlier, later or differently, they may reveal broader environmental change.`,
    sections: [
      {
        heading: "Build Your Personal Nature Calendar",
        body: `Record events such as:
• First spring flowers · First new leaves
• First butterflies · First migrating birds
• Nesting periods · First fruit
• Harvest dates · First autumn colors
• Leaf fall · First frost · First snow
• Frozen and thawing lakes
• Rainy season beginning · Dry season beginning
• Monsoon arrival · Wildfire season
• Migration arrivals and departures`,
      },
      {
        heading: "Compare Year to Year",
        body: `One unusual year does not necessarily indicate climate change. But repeated observations over many years become much more meaningful.

Imagine a community recording for 20 years: first flowering date · first migratory bird · first frost · river level · harvest date · butterfly appearance · first snowfall. That creates a valuable local environmental history.`,
      },
      {
        heading: "From Observation to Understanding",
        body: `NaturePulse should encourage people to ask: what was normal here five years ago? Ten years ago? Twenty years ago?

Old photographs can help too. Family albums, farm photographs and historical images may reveal former shorelines, forests, rivers, snow cover, vegetation and development.`,
      },
    ],
    outro: "Nature is telling us: climate change becomes visible when today's observations are compared with yesterday's memories.",
  },
};
